import React from "react";
import { BsToggleOff, BsToggleOn } from "react-icons/bs";
import { FaEdit, FaTrash } from "react-icons/fa";

import InlineWeightEditor from "../ui/InlineWeightEditor";
import PeseePager from "./PeseePager";

import { formatWeight, genreKey, getGradeClasses } from "../utils/peseeUtils";

const thClass =
  "px-3 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-300";
const tdClass = "px-3 py-3 text-sm text-slate-900 dark:text-slate-100";

const PresenceButton = ({ present, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex min-h-10 items-center gap-2 rounded-xl px-2 transition hover:bg-slate-100 dark:hover:bg-white/10"
  >
    {present ? (
      <BsToggleOn className="text-3xl text-emerald-500" />
    ) : (
      <BsToggleOff className="text-3xl text-slate-400" />
    )}
    <span className="text-xs font-bold text-slate-600 dark:text-slate-300">
      {present ? "Présent" : "Absent"}
    </span>
  </button>
);

const GenderBadge = ({ gender }) => {
  const key = genreKey(gender);

  return (
    <span
      className={`inline-flex min-w-12 items-center justify-center rounded-full px-2 py-1 text-xs font-bold ${
        key === "M"
          ? "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300"
          : key === "F"
          ? "bg-pink-100 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300"
          : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
      }`}
    >
      {String(gender || "—").toUpperCase()}
    </span>
  );
};

const GradeBadge = ({ grade }) => (
  <span
    className={`inline-flex items-center justify-center rounded-full px-2 py-1 text-xs font-bold ${getGradeClasses(
      grade
    )}`}
    title={grade || ""}
  >
    {grade || "—"}
  </span>
);

const MobileCard = ({
  row,
  onTogglePresence,
  onSaveWeight,
  onEdit,
  onDelete,
}) => {
  const fullName = `${row.nom_competiteur || ""} ${
    row.prenom_competiteur || ""
  }`.trim();

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-bold text-slate-950 dark:text-white">
            {fullName || "Compétiteur"}
          </h3>

          <p className="mt-0.5 truncate text-xs font-semibold text-slate-500 dark:text-slate-400">
            {row._club || "Sans club"}
          </p>
        </div>

        <PresenceButton
          present={!!row.is_present}
          onClick={() => onTogglePresence(row.id)}
        />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-xl bg-slate-50 p-2 dark:bg-white/5">
          <p className="text-slate-400">Naissance</p>
          <p className="font-bold">
            {row.ddn_competiteur
              ? new Date(row.ddn_competiteur).toLocaleDateString("fr-FR")
              : "—"}
          </p>
        </div>

        <div className="rounded-xl bg-slate-50 p-2 dark:bg-white/5">
          <p className="text-slate-400">Licence</p>
          <p className="truncate font-bold">{row.licence_competiteur || "—"}</p>
        </div>

        <div className="rounded-xl bg-slate-50 p-2 dark:bg-white/5">
          <p className="text-slate-400">Cat. âge</p>
          <p className="font-bold">{row._ageCat || "—"}</p>
        </div>

        <div className="rounded-xl bg-slate-50 p-2 dark:bg-white/5">
          <p className="text-slate-400">Cat. poids</p>
          <p className="font-bold">{row._weightCat || "—"}</p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <GenderBadge gender={row.genre_competiteur} />
        <GradeBadge grade={row.grade_competiteur} />
        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {row._discipline || "Judo"}
        </span>
      </div>

      <div className="mt-3">
        <InlineWeightEditor
          value={row.poids_competiteur}
          onSave={(value) => onSaveWeight(row.id, value)}
        />
      </div>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => onEdit(row)}
          className="inline-flex min-h-10 flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-bold transition hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5"
        >
          <FaEdit className="h-3.5 w-3.5" />
          Éditer
        </button>

        <button
          type="button"
          onClick={() => onDelete(row.id)}
          className="inline-flex min-h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
        >
          <FaTrash className="h-3.5 w-3.5" />
          Supprimer
        </button>
      </div>
    </article>
  );
};

const PeseeTable = ({
  rows,
  page,
  perPage,
  total,
  totalPages,
  onPageChange,
  onPerPageChange,
  onTogglePresence,
  onSaveWeight,
  onEdit,
  onDelete,
}) => {
  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-slate-900 sm:p-4">
      <div className="grid gap-3 lg:hidden">
        {rows.map((row) => (
          <MobileCard
            key={row.id}
            row={row}
            onTogglePresence={onTogglePresence}
            onSaveWeight={onSaveWeight}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}

        {!rows.length && (
          <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
            Aucun compétiteur avec ces filtres.
          </div>
        )}
      </div>

      <div className="hidden overflow-x-auto lg:block">
        <table className="w-full min-w-[1180px]">
          <thead className="bg-slate-50 dark:bg-white/[0.04]">
            <tr>
              <th className={thClass}>Présence</th>
              <th className={thClass}>Nom</th>
              <th className={thClass}>Prénom</th>
              <th className={thClass}>Licence</th>
              <th className={thClass}>Naissance</th>
              <th className={thClass}>Club</th>
              <th className={thClass}>Genre</th>
              <th className={thClass}>Poids</th>
              <th className={thClass}>Grade</th>
              <th className={thClass}>Cat. âge</th>
              <th className={thClass}>Cat. poids</th>
              <th className={`${thClass} text-right`}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-slate-100 odd:bg-white even:bg-slate-50/70 dark:border-white/10 dark:odd:bg-slate-900 dark:even:bg-slate-900/70"
              >
                <td className={tdClass}>
                  <PresenceButton
                    present={!!row.is_present}
                    onClick={() => onTogglePresence(row.id)}
                  />
                </td>

                <td className={`${tdClass} font-bold`}>
                  {row.nom_competiteur}
                </td>

                <td className={tdClass}>{row.prenom_competiteur}</td>
                <td className={tdClass}>{row.licence_competiteur || "—"}</td>

                <td className={tdClass}>
                  {row.ddn_competiteur
                    ? new Date(row.ddn_competiteur).toLocaleDateString("fr-FR")
                    : "—"}
                </td>

                <td className={`${tdClass} max-w-[220px] truncate`}>
                  {row._club || "—"}
                </td>

                <td className={tdClass}>
                  <GenderBadge gender={row.genre_competiteur} />
                </td>

                <td className={tdClass}>
                  <InlineWeightEditor
                    value={row.poids_competiteur}
                    onSave={(value) => onSaveWeight(row.id, value)}
                  />
                </td>

                <td className={tdClass}>
                  <GradeBadge grade={row.grade_competiteur} />
                </td>

                <td className={tdClass}>{row._ageCat || "—"}</td>
                <td className={tdClass}>{row._weightCat || "—"}</td>

                <td className={`${tdClass} text-right`}>
                  <div className="inline-flex gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(row)}
                      className="inline-flex min-h-9 items-center gap-2 rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold transition hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5"
                    >
                      <FaEdit className="h-3.5 w-3.5" />
                      Éditer
                    </button>

                    <button
                      type="button"
                      onClick={() => onDelete(row.id)}
                      className="inline-flex min-h-9 items-center gap-2 rounded-xl bg-rose-600 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-rose-700"
                    >
                      <FaTrash className="h-3.5 w-3.5" />
                      Supprimer
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {!rows.length && (
              <tr>
                <td
                  colSpan={12}
                  className="px-3 py-12 text-center text-sm text-slate-500 dark:text-slate-400"
                >
                  Aucun compétiteur avec ces filtres.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <PeseePager
        page={page}
        perPage={perPage}
        total={total}
        totalPages={totalPages}
        onPageChange={onPageChange}
        onPerPageChange={onPerPageChange}
      />
    </div>
  );
};

export default PeseeTable;